import { useEffect, useState } from 'react'
import {
  ExternalLink,
  Users,
  Calendar,
  ClipboardList,
  Clock,
  Building2,
  FileText,
  Loader2,
  AlertCircle,
  BookOpen,
} from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import { apiGet } from '../../services/api'

interface EagleVisionStatus {
  enabled: boolean
  base_url: string | null
}

export default function HRManagementPage() {
  const { user } = useAuthStore()
  const { can } = usePermissions()
  const canOpenLearning = can('pwa_learning', 'view')
  const [evStatus, setEvStatus] = useState<EagleVisionStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadEVStatus()
  }, [])

  const loadEVStatus = async () => {
    try {
      const status = await apiGet<EagleVisionStatus>('/auth/eagle-vision/status')
      setEvStatus(status)
    } catch (err) {
      console.error('Failed to load Eagle Vision status:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const evPortalUrl = 'https://eaglevisionpro.com'
  const evToken = localStorage.getItem('ev_token')

  const hrModules = [
    { id: 'employees', label: 'Employees', description: 'Manage employee records, profiles, and information', icon: Users, path: '/account/employees' },
    { id: 'departments', label: 'Departments', description: 'Manage organizational departments and teams', icon: Building2, path: '/account/departments' },
    { id: 'attendance', label: 'Attendance Reports', description: 'View attendance records and reports', icon: Clock, path: '/account/attendances' },
    { id: 'shifts', label: 'Shift Management', description: 'Manage shifts and shift rosters', icon: Calendar, path: '/account/shifts' },
    { id: 'leaves', label: 'Leave Management', description: 'Manage leave requests and approvals', icon: ClipboardList, path: '/account/leaves' },
    { id: 'reports', label: 'HR Reports', description: 'View comprehensive HR reports and analytics', icon: FileText, path: '/account/reports' },
    ...(canOpenLearning ? [
      { id: 'knowledge-base', label: 'Knowledge Base', description: 'Open assigned articles in Eagle Vision Pro', icon: BookOpen, path: '/account/knowledgebase' },
      { id: 'learning-center', label: 'Learning Center', description: 'Open assigned questionnaires in Eagle Vision Pro', icon: ClipboardList, path: '/account/questionnaire' },
    ] : []),
  ]

  const handleOpenModule = (path: string) => {
    const url = new URL(path, evPortalUrl)
    const handoffAlreadyUsed = localStorage.getItem('ev_sso_handoff_used') === 'true'
    if (evToken && !handoffAlreadyUsed) {
      url.searchParams.set('token', evToken)
      localStorage.setItem('ev_sso_handoff_used', 'true')
    }
    window.open(url.toString(), '_blank', 'noopener,noreferrer')
  }

  const handleOpenPortal = () => {
    handleOpenModule('/account')
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (!evStatus?.enabled) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-yellow-800 mb-2">Eagle Vision Integration Not Enabled</h2>
          <p className="text-yellow-700">
            HR Management features require Eagle Vision integration to be enabled.
            Please contact your administrator.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">HR Management</h1>
        <p className="text-gray-600 mt-1">Access HR modules powered by Eagle Vision Pro</p>
      </div>

      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-blue-700">
              Logged in as <span className="font-semibold capitalize">{user?.ev_designation_name || user?.ev_role || user?.role}</span>
            </p>
          </div>
          <button
            onClick={handleOpenPortal}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <span>Open Eagle Vision Portal</span>
            <ExternalLink className="h-4 w-4" />
          </button>
        </div>
      </div>


      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {hrModules.map((module) => (
          <button
            key={module.id}
            onClick={() => handleOpenModule(module.path)}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-left hover:shadow-md hover:border-primary-300 transition-all group"
          >
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary-50 rounded-lg group-hover:bg-primary-100 transition-colors">
                <module.icon className="h-6 w-6 text-primary-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">{module.label}</h3>
                  <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-primary-500" />
                </div>
                <p className="text-sm text-gray-500 mt-1">{module.description}</p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}
